/**
 * CharIframeProxy
 *
 * Host-side relay that bridges MCP server-to-client responses from the page's
 * TabServerTransport back to the iframe (embedded agent).
 *
 * Client-to-server messages (iframe → host) are handled directly by
 * TabServerTransport via allowedOrigins, so no relay is needed in that
 * direction.
 *
 * Zero runtime dependencies — uses only browser postMessage APIs.
 */

export class CharIframeProxy {
	private _cleanup: (() => void)[] = []

	constructor(
		private _iframe: HTMLIFrameElement,
		private _iframeOrigin: string,
		private _channelId = 'mcp-default'
	) {}

	start(): void {
		// window → iframe: relay server-to-client messages so the iframe client transport picks them up
		const fromTab = (event: MessageEvent) => {
			if (event.source !== window) return
			const d = event.data
			if (!d || d.channel !== this._channelId || d.type !== 'mcp' || d.direction !== 'server-to-client') return
			if (d._proxied) return // defense-in-depth: don't relay our own relayed messages
			if (!this._iframe.contentWindow) {
				console.error('[CharIframeProxy] Cannot relay to iframe: contentWindow is null')
				return
			}
			this._iframe.contentWindow.postMessage(d, this._iframeOrigin)
		}

		window.addEventListener('message', fromTab)
		this._cleanup = [
			() => window.removeEventListener('message', fromTab),
		]
	}

	destroy(): void {
		for (const fn of this._cleanup) fn()
		this._cleanup.length = 0
	}
}
